import { FC, useEffect } from "react";
import { CRMChannel } from "@jugl-web/rest-api";
import {
  CRM_GMAIL_OAUTH_PENDING_REDIRECT_KEY,
  ENTITY_ID_KEY,
  getStorageItem,
} from "@jugl-web/utils/storage";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useNavigateToRoleBasedInitialPage } from "@web-src/modules/navigation/hooks/useNavigateToRoleBasedInitialPage";
import { Loader } from "@jugl-web/ui-kit";
import { useEntitiesListProvider } from "@web-src/modules/entities/providers/EntitiesListProvider/useEntitiesListProvider";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { useParams } from "react-router-dom";
import { getLoginFlow } from "@jugl-web/utils/hooks/useLoginFlow";

const GMAIL_OAUTH_PENDING_REDIRECT_TTL_MS = 10 * 60 * 1000;

export const NavigateToDefaultEntity: FC = () => {
  const { navigateToPage } = useNavigation();
  const {
    entitiesState: { entities: list, isFetching: isEntitiesFetching },
    entityInvitesState: { entityInvites, isFetching: isEntityInvitesFetching },
  } = useEntitiesListProvider();
  const navigateToRoleBasedInitialPage = useNavigateToRoleBasedInitialPage();

  const [defaultEntityId] = useLocalStorage(ENTITY_ID_KEY, "");
  const { targetEntityId: paramsEntityId } = useParams<{
    targetEntityId: string;
  }>();
  const selectedEntity = paramsEntityId
    ? list?.find((entity) => entity.id === paramsEntityId)
    : list?.find((entity) => entity.id === defaultEntityId) || list?.[0];

  useEffect(() => {
    if (!isEntitiesFetching && !isEntityInvitesFetching) {
      if (selectedEntity) {
        // the Gmail OAuth backend can only redirect back to the bare app
        // origin; a pending-redirect flag saved before leaving for OAuth
        // (EmailAccountProvider) tells us to land on the inbox instead of
        // the role-based default page
        const pendingGmailRedirect = getStorageItem<{
          entityId: string;
          createdAt: number;
        }>(CRM_GMAIL_OAUTH_PENDING_REDIRECT_KEY);

        if (pendingGmailRedirect) {
          localStorage.removeItem(CRM_GMAIL_OAUTH_PENDING_REDIRECT_KEY);

          const isFresh =
            Date.now() - pendingGmailRedirect.createdAt <
            GMAIL_OAUTH_PENDING_REDIRECT_TTL_MS;
          const targetEntityExists = list?.some(
            (entity) => entity.id === pendingGmailRedirect.entityId
          );

          // the connection result (auth_success/result) is captured at app boot
          // by captureGmailOAuthResult, before this redirect chain strips the
          // query string; here we only route the user to the inbox

          if (isFresh && targetEntityExists && !paramsEntityId) {
            navigateToPage(
              "clientSupportChannel",
              { channel: CRMChannel.email },
              { entityId: pendingGmailRedirect.entityId }
            );
            return;
          }
        }

        navigateToRoleBasedInitialPage(selectedEntity);
      } else if (getLoginFlow() === "cx") {
        navigateToPage("cxOnboarding");
      } else if (entityInvites && entityInvites.length === 0) {
        navigateToPage("authSignUpPurpose");
      } else {
        navigateToPage("authInvitations");
      }
    }
  }, [
    isEntitiesFetching,
    isEntityInvitesFetching,
    selectedEntity,
    entityInvites,
    list,
    paramsEntityId,
    navigateToRoleBasedInitialPage,
    navigateToPage,
  ]);

  return <Loader className="h-screen w-screen" />;
};
